import type { SortingState } from "@tanstack/react-table"
import { buildCollectionTableKey } from "../utils/collectionPaths"

export type TableRowEntry = { slug?: string; data?: unknown; sorting?: SortingState }

const EMPTY_SORTING: SortingState = []

export function getTableSortingForRoute(
  tables: TableRowEntry[] | null | undefined,
  activeTableKey: string,
  routeCollectionSlug: string,
  routeOrgNorm: string
): SortingState {
  const entry = findTableEntryForRoute(
    tables,
    activeTableKey,
    routeCollectionSlug,
    routeOrgNorm
  )
  return Array.isArray(entry?.sorting) ? [...entry.sorting] : EMPTY_SORTING
}

/** Slug keys that may hold the same collection table (URL tableKey vs stored Redux slug). */
export function getCollectionTableSlugCandidates(
  activeTableKey: string,
  routeCollectionSlug: string,
  routeOrgNorm: string
): string[] {
  const keys = new Set<string>()
  if (activeTableKey) keys.add(activeTableKey)
  if (routeCollectionSlug) {
    keys.add(routeCollectionSlug)
    const scoped = buildCollectionTableKey(routeCollectionSlug, routeOrgNorm || null)
    if (scoped) keys.add(scoped)
  }
  return [...keys]
}

export function findTableEntryForRoute(
  tables: TableRowEntry[] | null | undefined,
  activeTableKey: string,
  routeCollectionSlug: string,
  routeOrgNorm: string
): TableRowEntry | null {
  if (!Array.isArray(tables) || tables.length === 0) return null

  for (const key of getCollectionTableSlugCandidates(
    activeTableKey,
    routeCollectionSlug,
    routeOrgNorm
  )) {
    const hit = tables.find((t) => t?.slug === key)
    if (hit) return hit
  }

  if (routeCollectionSlug) {
    const suffix = `/${routeCollectionSlug}`
    const bySuffix = tables.find(
      (t) => typeof t.slug === "string" && t.slug.endsWith(suffix)
    )
    if (bySuffix) return bySuffix
  }

  return null
}

export function getTableLoadingStateForRoute(
  loadingBySlug: Record<string, boolean> | null | undefined,
  activeTableKey: string,
  routeCollectionSlug: string,
  routeOrgNorm: string
): { isLoading: boolean; isSettled: boolean } {
  const loading = loadingBySlug ?? {}
  const keys = getCollectionTableSlugCandidates(
    activeTableKey,
    routeCollectionSlug,
    routeOrgNorm
  )
  return {
    isLoading: keys.some((k) => loading[k] === true),
    isSettled: keys.some(
      (k) => Object.prototype.hasOwnProperty.call(loading, k) && loading[k] === false
    ),
  }
}

export type TableViewLoadingInput = {
  currentSlug: string
  /** Redux `loadingBySlug` key (tableKey); defaults to `currentSlug` when omitted */
  tableLoadingKey?: string
  routeCollectionSlug?: string
  routeOrgNorm?: string
  meta: unknown
  table: { data?: unknown } | null | undefined
  tableError: unknown
  tableLoadingBySlug: Record<string, boolean> | undefined | null
  forceLoadingTest?: boolean
}

export type TableViewLoadingState = {
  hasValidMeta: boolean
  hasAttemptedLoad: boolean
  isLoading: boolean
  hasData: boolean
  isRequestLoading: boolean
  isTableLoading: boolean
}

export function computeTableViewLoadingState(
  input: TableViewLoadingInput
): TableViewLoadingState {
  const {
    currentSlug,
    tableLoadingKey,
    routeCollectionSlug = "",
    routeOrgNorm = "",
    meta,
    table,
    tableError,
    tableLoadingBySlug,
    forceLoadingTest = false,
  } = input

  const loadingKey = tableLoadingKey ?? currentSlug
  const { isLoading: isRequestLoading, isSettled: requestSettledForKey } =
    getTableLoadingStateForRoute(
      tableLoadingBySlug,
      loadingKey,
      routeCollectionSlug || currentSlug,
      routeOrgNorm
    )

  const metaForState =
    meta && typeof meta === 'object' && !Array.isArray(meta) ? meta : null
  const hasValidMeta = Boolean(
    metaForState && Object.keys(metaForState).length > 0
  )
  const tableForState =
    table && typeof table === 'object' && !Array.isArray(table) ? table : null
  const hasAttemptedLoad = Boolean(
    currentSlug &&
    (tableError ||
      hasValidMeta ||
      Boolean(tableForState?.slug) ||
      requestSettledForKey)
  )
  const isLoading = !hasValidMeta && !hasAttemptedLoad
  const hasData = Boolean(
    tableForState?.data && Array.isArray(tableForState.data)
  )
  // Avoid a perpetual table overlay once meta exists: rely on Redux loadingBySlug + TABLE_FAILURE/timeout to clear.
  const isTableLoading =
    forceLoadingTest ||
    isRequestLoading ||
    (Boolean(currentSlug) && !hasData && !tableError && !hasValidMeta)

  return {
    hasValidMeta,
    hasAttemptedLoad,
    isLoading,
    hasData,
    isRequestLoading,
    isTableLoading,
  }
}
